import axios from "axios";
import { UTILITY_BASE } from "../api/bases";
import { practiceName, practiceShortName, practiceSupportDesk } from "../config/practice";

/*
 * The login page's Support ticket (auth/login.jsx).
 *
 * v1 POST support/create-ticket files the ticket in the practice's
 * ticket_master, which nothing reads (no read route, no care screen). So,
 * where the practice names a support mailbox (practiceSupportDesk().email,
 * 7825: the BrainyMed admin mailbox OSA's own build used), a copy of the
 * ticket is mailed there through the utility mailer (UTILITY_BASE/send-email,
 * the one care's Forgot Password already calls without a login). A practice
 * without a mailbox sends nothing extra.
 */

// ticket_master.priority: 1 is the most urgent. Care labelled 3 "Urgent", so
// a user who picked Urgent filed the LOWEST priority (every practice).
export const SUPPORT_SEVERITIES = [
  { value: 1, label: "High (Urgent)" },
  { value: 2, label: "Medium" },
  { value: 3, label: "Low" },
];

export const severityLabel = (value) =>
  (SUPPORT_SEVERITIES.find((s) => s.value === Number(value)) || {}).label || "";

const HTML_ESCAPES = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };

// User text goes into the email's HTML: escaped, never markup.
export const escapeHtml = (value) =>
  String(value ?? "").replace(/[&<>"']/g, (c) => HTML_ESCAPES[c]);

// A mail header is one line (no CR/LF from the form).
const oneLine = (value) => String(value ?? "").replace(/[\r\n]+/g, " ").trim();

// The ticket's subject: "[Category] subject" when the form has a category
// (OSA's format), else the subject as typed.
export const ticketSubject = (subject, category) =>
  category ? `[${category}] ${subject}` : subject;

export const supportEmailSubject = (subject) =>
  oneLine(`Support Ticket ${practiceShortName()}: ${subject}`);

export function supportEmailBody({ ticketId, name, email, category, subject, severity, details }) {
  const rows = [
    ["Practice", practiceName()],
    ticketId ? ["Ticket", ticketId] : null,
    ["Name", name],
    ["Email", email],
    category ? ["Category", category] : null,
    ["Severity", severityLabel(severity)],
    ["Subject", subject],
  ].filter(Boolean);
  const cell = "padding:6px 8px;border-bottom:1px solid #e5e7eb;vertical-align:top;";
  const table = rows
    .map(([k, v]) => `<tr><td style="${cell}color:#6b7280;width:30%;">${escapeHtml(k)}</td><td style="${cell}font-weight:600;">${escapeHtml(v)}</td></tr>`)
    .join("");
  const text = escapeHtml(details).replace(/\r?\n/g, "<br />");
  return (
    `<div style="font-family:Arial,sans-serif;color:#374151;max-width:560px;">` +
    `<h2 style="font-size:18px;margin:0 0 12px;">Support ticket from the ${escapeHtml(practiceShortName())} login page</h2>` +
    `<table style="width:100%;border-collapse:collapse;font-size:13px;">${table}</table>` +
    `<p style="font-size:14px;line-height:1.6;margin:16px 0 0;">${text}</p>` +
    `</div>`
  );
}

// Mails the ticket to the practice's support desk. Resolves true when the
// mailer accepted it, false when the practice has no mailbox or the send
// failed; never throws, so the filed ticket always stands.
export async function notifySupportDesk(ticket) {
  const to = practiceSupportDesk().email;
  if (!to) return false;
  try {
    const formData = new FormData();
    formData.append("subject", supportEmailSubject(ticket.subject));
    formData.append("to", to);
    formData.append("message", supportEmailBody(ticket));
    const resp = await axios.post(`${UTILITY_BASE}/send-email`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return resp?.status === 200;
  } catch (e) {
    console.error("Support ticket email failed:", e);
    return false;
  }
}
